import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { CallSplit } from "@mui/icons-material";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { CommentsField } from "@/shared/components/CommentsField";
import { QuickDelayCodeButtons } from "@/shared/components/QuickDelayCodeButtons";
import { WriteLockTooltip } from "@/shared/components/WriteLockTooltip";
import { EDITABLE_FIELD_SX } from "@/pages/reports/rollshop-operations/components/compactDialog";
import { useOperationalWritesLocked } from "@/shared/hooks/useOperationalWritesLocked";
import { DelayCategorySelector } from "@/shared/selectors/DelayCategorySelector";
import {
	DelayCodeTreePicker,
	DelayCodeFilterMode,
} from "@/shared/selectors/DelayCodeTreePicker";
import {
	ErrorModal,
	getMomentTz,
	HelmetDexteel,
	MesfModal,
	TimeService,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { useDelayById, useJustifyDelay, useUpsertDelay } from "../../hooks";
import { DelayByShift } from "../../models/DelayByShift";
import { DelayCode } from "@/shared/models/DelayCode";

const moment = getMomentTz();

const MODAL_TITLES: Record<string, string> = {
	justify: "Stop Declaration",
	edit: "EDIT DELAY",
	new: "ADD DELAY",
};

const HELMET_TITLES: Record<string, string> = {
	justify: "DECLARE STOP",
	edit: "EDIT DELAY",
	new: "ADD DELAY",
};

type Props = {
	showModal: "new" | "edit" | "delete" | "justify" | "split" | "";
	onHide: (shouldUpdate: boolean) => void;
	delayId: number | null;
	assetId?: number;
	suffixTitle?: string;
	onSplit?: (delayId: number) => void;
};

export const UpsertJustifyDelayModal = ({
	showModal,
	onHide,
	delayId,
	assetId,
	suffixTitle = "STOPS",
	onSplit,
}: Props) => {
	const writesLocked = useOperationalWritesLocked();
	const [delay, setDelay] = useState<DelayByShift | null>(null);
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [title, setTitle] = useState("");
	const [duration, setDuration] = useState<string | number>("");
	const { shiftInfo } = useShiftNavigator();
	const [searchParams] = useSearchParams();

	// Passing the Ball: Read mode from URL params
	// - passingMode=operator: Show only branch codes (operators selecting main area)
	// - passingMode=specialist&branchCodeId=X: Show only children of branch X (specialists)
	// - No passingMode: Show all codes (manual browsing/editing)
	const { filterMode, branchCodeId } = useMemo((): {
		filterMode: DelayCodeFilterMode;
		branchCodeId: number | null;
	} => {
		const passingMode = searchParams.get("passingMode");
		const branchCodeIdParam = searchParams.get("branchCodeId");

		if (passingMode === "operator") {
			return { filterMode: "branches-only", branchCodeId: null };
		}

		if (passingMode === "specialist" && branchCodeIdParam) {
			return {
				filterMode: "children-of",
				branchCodeId: Number(branchCodeIdParam),
			};
		}

		// Default: show all codes (manual editing or no passing the ball)
		return { filterMode: "all", branchCodeId: null };
	}, [searchParams]);

	// Query hook for fetching delay by ID
	const {
		data: delays,
		isLoading,
		isSuccess,
		error: queryError,
	} = useDelayById({ delayId });

	// Mutation hooks
	const { justifyDelay, isJustifying } = useJustifyDelay({
		onSuccess: () => {
			setNotificationMessage("Stop was declared successfully");
			onHide(true);
		},
		onError: (err) => setError(err.message),
	});

	const { upsertDelay, isUpserting } = useUpsertDelay({
		onSuccess: () => {
			setNotificationMessage(
				showModal === "new"
					? "Stop was added successfully"
					: "Stop was edited successfully",
			);
			onHide(true);
		},
		onError: (err) => setError(err.message),
	});

	const isSubmitLoading = isJustifying || isUpserting;

	// Handle query errors
	useEffect(() => {
		if (queryError) {
			setError(queryError.message);
		}
	}, [queryError]);

	const { register, setValue, control, handleSubmit, getValues, reset, watch } =
		useForm<DelayByShift>({
			defaultValues: {},
		});

	// Which open (mode + delay) already filled the form, so a refetch of the
	// same delay does not overwrite the user's input.
	const loadedKeyRef = useRef<string | null>(null);

	const start = watch("Start");
	const end = watch("End");
	const selectedCodeId = watch("DelayCodeId");

	const handleQuickButtonSelect = async (code: DelayCode) => {
		setValue("DelayCodeId", code.DelayCodeId);
		setValue("DelayCategoryId", code.DelayCategoryId);
		// Auto-submit when quick button is clicked
		await handleSubmit(onSubmit)();
	};

	const onSubmit: SubmitHandler<DelayByShift> = (data: DelayByShift) => {
		if (showModal === "justify") {
			justifyDelay({
				delayId: data.DelayId!,
				delayCodeId: data.DelayCodeId ?? null,
				delayCategoryId: data.DelayCategoryId ?? null,
				comments: data.Comments ?? null,
			});
		} else {
			upsertDelay({
				delayId: data.DelayId === 0 ? null : data.DelayId,
				assetId: data.AssetId,
				start: data.Start,
				end: data.End,
				delayCodeId: data.DelayCodeId,
				delayCategoryId: data.DelayCategoryId,
				comments: data.Comments,
			});
		}
	};

	useEffect(() => {
		if (showModal !== "" && showModal !== "delete") {
			setTitle(MODAL_TITLES[showModal] || "STOP");

			const defaultStart = shiftInfo?.CurrentStart || null;
			const defaultEnd = new Date(shiftInfo?.CurrentStart!) || null;
			defaultEnd.setHours(defaultEnd.getHours() + 1);

			const loadKey = `${showModal}-${delay?.DelayId}`;
			if (
				delay &&
				showModal === "justify" &&
				loadedKeyRef.current === loadKey
			) {
				// Live refresh of an open stop (Mill Setup polls it): only the
				// read-only times move; what the user is typing stays.
				setValue("Start", delay.Start ?? defaultStart);
				setValue("End", delay.End ?? null);
			} else if (delay && showModal !== "new") {
				loadedKeyRef.current = loadKey;
				setValue("DelayCodeId", delay.DelayCodeId);
				setValue("DelayId", delay.DelayId);
				setValue(
					"Start",
					delay.Start === null || delay.Start === undefined
						? defaultStart
						: delay.Start,
				);
				setValue(
					"End",
					delay.End === null || delay.End === undefined ? null : delay.End,
				);
				setValue("DelayCategoryId", delay.DelayCategoryId);
				setValue("Comments", delay.Comments);
			} else {
				loadedKeyRef.current = null;
				reset();
				setValue("Start", defaultStart);
				setValue("End", defaultEnd);
			}
			setValue("AssetId", assetId ?? 1);
		} else {
			loadedKeyRef.current = null;
			reset();
		}
	}, [showModal, delay]);

	useEffect(() => {
		if (start && end) {
			const dur = moment.duration(moment(end).diff(moment(start)));
			const hours = Math.floor(dur.asHours());
			const minutes = dur.minutes();
			const seconds = dur.seconds();
			if (hours > 0) {
				setDuration(`${hours}h ${minutes}m ${seconds}s`);
			} else if (minutes > 0) {
				setDuration(`${minutes}m ${seconds}s`);
			} else {
				setDuration(`${seconds}s`);
			}
		} else {
			setDuration("");
		}
	}, [start, end]);

	useEffect(() => {
		if (isSuccess) {
			if (delays && delays[0]) {
				setDelay(delays[0]);
			}
		}
	}, [isSuccess, delays]);

	return (
		<>
			{(showModal === "justify" ||
				showModal === "edit" ||
				showModal === "new") && (
				<HelmetDexteel
					title={`${HELMET_TITLES[showModal] || "STOP"} - ${suffixTitle}`}
				/>
			)}
			<MesfModal
				maxWidth="xl"
				open={showModal !== "" && showModal !== "delete"}
				handleClose={() => {
					onHide(false);
				}}
				id="upsertStop"
				title={title}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content style={{ padding: "15px 30px" }} dividers>
						{/* Only show loading when editing/justifying an existing delay */}
						{showModal !== "new" && isLoading && <CenteredLazyLoading />}
						{(showModal === "new" || !isLoading) && (
							<>
								{/* Row 1: Start, End, Duration, Category */}
								<Grid2 container spacing={1} style={{ alignItems: "center" }}>
									<Grid2
										size={{ xs: 12, md: 3.5 }}
										sx={showModal === "justify" ? undefined : EDITABLE_FIELD_SX}
									>
										<Box style={{ height: 35 }}>
											<Controller
												name="Start"
												control={control}
												rules={{
													deps: ["End"],
													required: "Start is required",
													validate: {
														notAfterEnd: (value) => {
															const endDate = watch("End");
															if (value && endDate && value > endDate) {
																return "Start cannot be after End.";
															}
															return true;
														},
														notInFuture: (value) => {
															if (value && value > new Date()) {
																return "Start cannot be in the future.";
															}
															return true;
														},
													},
												}}
												render={({ field, fieldState: { error } }) => (
													<DateTimePicker
														disabled={showModal === "justify"}
														label="Start"
														timezone={TimeService.getInstance().getServerTimeZone()}
														format="MM/DD/YYYY HH:mm:ss"
														ampm={false}
														views={[
															"year",
															"month",
															"day",
															"hours",
															"minutes",
															"seconds",
														]}
														timeSteps={{ minutes: 1, seconds: 1 }}
														value={field.value ? moment(field.value) : null}
														onChange={field.onChange}
														slotProps={{
															textField: {
																size: "small",
																margin: "dense",
																error: !!error,
																helperText: error?.message,
															},
														}}
													/>
												)}
											/>
										</Box>
									</Grid2>
									<Grid2
										size={{ xs: 12, md: 3.5 }}
										sx={showModal === "justify" ? undefined : EDITABLE_FIELD_SX}
									>
										<Box style={{ height: 35 }}>
											<Controller
												name="End"
												control={control}
												rules={{
													deps: ["Start"],
													validate: {
														notBeforeStart: (value) => {
															const startDate = watch("Start");
															if (value && startDate && value < startDate) {
																return "End cannot be before Start.";
															}
															return true;
														},
														notInFuture: (value) => {
															if (value && value > new Date()) {
																return "End cannot be in the future.";
															}
															return true;
														},
														notSameAsStart: (value) => {
															const startDate = watch("Start");
															if (
																value &&
																startDate &&
																moment(value).isSame(moment(startDate))
															) {
																return "End cannot be the same as Start.";
															}
															return true;
														},
													},
												}}
												render={({ field, fieldState: { error } }) => (
													<DateTimePicker
														disabled={showModal === "justify"}
														label="End"
														format="MM/DD/YYYY HH:mm:ss"
														ampm={false}
														timezone={TimeService.getInstance().getServerTimeZone()}
														views={[
															"year",
															"month",
															"day",
															"hours",
															"minutes",
															"seconds",
														]}
														timeSteps={{ minutes: 1, seconds: 1 }}
														value={field.value ? moment(field.value) : null}
														onChange={field.onChange}
														slotProps={{
															textField: {
																size: "small",
																margin: "dense",
																error: !!error,
																helperText: error?.message,
															},
														}}
													/>
												)}
											/>
										</Box>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 2 }} marginTop={1.3}>
										<Box style={{ height: 45 }}>
											<TextField
												label="Duration"
												disabled
												value={duration}
												margin="dense"
												autoComplete="off"
												fullWidth
												size="small"
											/>
										</Box>
									</Grid2>
									<Grid2
										size={{ xs: 12, md: 3 }}
										marginTop={1.3}
										sx={EDITABLE_FIELD_SX}
									>
										<Box style={{ height: 45 }}>
											<Controller
												name="DelayCategoryId"
												control={control}
												render={({ field, fieldState: { error } }) => (
													<DelayCategorySelector
														label="Category"
														value={field.value}
														onChange={field.onChange}
														fieldError={error}
														assetId={assetId ?? 1}
														disabled={false}
													/>
												)}
											/>
										</Box>
									</Grid2>
								</Grid2>

								{/* Row 2: Code Tree Picker - Always visible with all codes */}
								<Grid2 container spacing={2} sx={{ mt: 2 }}>
									<Grid2 size={{ xs: 12 }} sx={EDITABLE_FIELD_SX}>
										<Controller
											name="DelayCodeId"
											control={control}
											render={({ field }) => (
												<DelayCodeTreePicker
													label="Code"
													assetId={getValues("AssetId")}
													value={field.value}
													onChange={(value, defaultCategoryId) => {
														field.onChange(value);
														setValue("DelayCategoryId", defaultCategoryId);
													}}
													showFullPath
													filterMode={
														filterMode === "branches-only" ? "all" : filterMode
													}
													branchCodeId={branchCodeId}
												/>
											)}
										/>
									</Grid2>
								</Grid2>

								{/* Row 3: Quick Selection Buttons */}
								{/* In operator mode: This is the ONLY way to select (branches only) */}
								{/* In other modes: Additional quick access to common codes */}
								<Grid2 container spacing={2} sx={{ mt: 2 }}>
									<Grid2 size={{ xs: 12 }}>
										<Typography
											variant="subtitle2"
											color="text.secondary"
											sx={{ mb: 1 }}
										>
											{filterMode === "branches-only"
												? "Select Area"
												: "Quick Selection"}
										</Typography>
										<WriteLockTooltip locked={writesLocked} fullWidth>
											<QuickDelayCodeButtons
												assetId={assetId ?? 1}
												selectedCodeId={selectedCodeId ?? null}
												onSelect={handleQuickButtonSelect}
												disabled={isSubmitLoading || writesLocked}
												filterMode={filterMode}
												branchCodeId={branchCodeId}
											/>
										</WriteLockTooltip>
									</Grid2>
								</Grid2>

								{/* Row 4: Comments */}
								<Grid2 container spacing={2} sx={{ mt: 2 }}>
									<Grid2 size={{ xs: 12 }} sx={EDITABLE_FIELD_SX}>
										<Controller
											name="Comments"
											control={control}
											render={({ field, fieldState: { error } }) => (
												<CommentsField
													value={field.value}
													onChange={field.onChange}
													fieldError={error}
													getValues={getValues}
													rows={{ minRows: 2, maxRows: 3 }}
													maxLength={500}
												/>
											)}
										/>
									</Grid2>
								</Grid2>

								{/* Row 5: Manual Delay checkbox */}
								<Grid2 container sx={{ mt: 1 }}>
									<Grid2 size={{ xs: 12 }}>
										<FormControlLabel
											control={
												<Checkbox
													{...register("IsDelayManual")}
													color="primary"
													checked={showModal === "new" || delay?.IsDelayManual}
													disabled={true}
												/>
											}
											label="Manual Delay"
										/>
									</Grid2>
								</Grid2>
							</>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 20px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid2>
							{showModal === "justify" &&
								delay?.Start &&
								delay?.End &&
								onSplit && (
									<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
										<WriteLockTooltip locked={writesLocked} fullWidth>
											<Button
												fullWidth
												variant="contained"
												color="success"
												startIcon={<CallSplit />}
												onClick={() => onSplit(delay.DelayId!)}
												disabled={writesLocked}
											>
												SPLIT
											</Button>
										</WriteLockTooltip>
									</Grid2>
								)}
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<WriteLockTooltip locked={writesLocked} fullWidth>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading ? (
												<Box component="span" sx={{ display: "flex" }}>
													<CircularProgress size="1rem" />
												</Box>
											) : undefined
										}
										disabled={isSubmitLoading || writesLocked}
										variant="contained"
										color="primary"
										type="submit"
									>
										Save
									</Button>
								</WriteLockTooltip>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
